import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Trash2, Edit2, ArrowRight, Columns, Database, Table, Grid, List } from 'lucide-react';
import storageService from '../../services/storageService';

const DataDictionary = () => {
  const [entities, setEntities] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null); // which field is selected in split view
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    fields: []
  });
  
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [newField, setNewField] = useState({
    name: '',
    code: '',
    dataType: 'string',
    format: 'text',
    mapping: { type: 'direct', source: '', table: '', column: '', formula: '' }
  });

  // For cascade selectors
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedTable, setSelectedTable] = useState('');
  const [availableTables, setAvailableTables] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);

  useEffect(() => {
    loadEntities();
    loadDataSources();
  }, []);

  useEffect(() => {
    if (selectedSource) {
      const source = dataSources.find(ds => ds.id === selectedSource);
      setAvailableTables(source?.tables || []);
      setSelectedTable('');
      setAvailableColumns([]);
    } else {
      setAvailableTables([]);
      setSelectedTable('');
      setAvailableColumns([]);
    }
  }, [selectedSource, dataSources]);

  useEffect(() => {
    if (selectedTable) {
      const source = dataSources.find(ds => ds.id === selectedSource);
      const table = source?.tables.find(t => t.name === selectedTable);
      setAvailableColumns(table?.columns || []);
    } else {
      setAvailableColumns([]);
    }
  }, [selectedTable, selectedSource, dataSources]);

  const loadEntities = () => {
    const data = storageService.getDataEntities();
    setEntities(data);
  };

  const loadDataSources = () => {
    const data = storageService.getDataSources();
    setDataSources(data);
  };

  const handleOpenModal = (entity = null) => {
    if (entity) {
      setEditingEntity(entity);
      setFormData({
        name: entity.name,
        code: entity.code,
        description: entity.description || '',
        fields: entity.fields || []
      });
      setSelectedFieldIndex(null);
    } else {
      setEditingEntity(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        fields: []
      });
      setSelectedFieldIndex(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEntity(null);
    setSelectedFieldIndex(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      fields: []
    });
    resetFieldForm();
  };

  const resetFieldForm = () => {
    setEditingFieldIndex(null);
    setNewField({
      name: '',
      code: '',
      dataType: 'string',
      format: 'text',
      mapping: { type: 'direct', source: '', table: '', column: '', formula: '' }
    });
    setSelectedSource('');
    setSelectedTable('');
  };

  const handleSave = () => {
    const entity = {
      ...formData,
      id: editingEntity?.id || `ent${Date.now()}`,
      createdAt: editingEntity?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    storageService.saveDataEntity(entity);
    loadEntities();
    handleCloseModal();
  };

  const handleDelete = (id) => {
    if (window.confirm('Bạn có chắc muốn xóa đối tượng này?')) {
      storageService.deleteDataEntity(id);
      loadEntities();
    }
  };

  const handleEditField = (index) => {
    const field = formData.fields[index];
    setEditingFieldIndex(index);
    setNewField(field);
    setSelectedFieldIndex(null); // Show form instead of detail view
    
    // Pre-populate cascade selectors if direct mapping
    if (field.mapping.type === 'direct' && field.mapping.source) {
      const parts = field.mapping.source.split('.');
      if (parts.length === 3) {
        const [sourceName, tableName] = parts;
        const source = dataSources.find(ds => ds.name === sourceName);
        if (source) {
          setSelectedSource(source.id);
          setSelectedTable(tableName);
        }
      }
    }
  };

  const handleAddOrUpdateField = () => {
    if (!newField.name || !newField.code) return;

    // Build mapping source string for direct type
    if (newField.mapping.type === 'direct' && selectedSource && selectedTable && newField.mapping.column) {
      const sourceName = dataSources.find(ds => ds.id === selectedSource)?.name;
      newField.mapping.source = `${sourceName}.${selectedTable}.${newField.mapping.column}`;
    }

    const fieldData = { ...newField, id: newField.id || `f${Date.now()}` };

    if (editingFieldIndex !== null) {
      // Update existing field
      const updatedFields = [...formData.fields];
      updatedFields[editingFieldIndex] = fieldData;
      setFormData({ ...formData, fields: updatedFields });
    } else {
      // Add new field
      setFormData({
        ...formData,
        fields: [...formData.fields, fieldData]
      });
    }

    resetFieldForm();
  };

  const handleRemoveField = (index) => {
    if (window.confirm('Bạn có chắc muốn xóa trường này?')) {
      setFormData({
        ...formData,
        fields: formData.fields.filter((_, i) => i !== index)
      });
    }
  };

  const handleColumnSelect = (columnName) => {
    setNewField({
      ...newField,
      mapping: {
        ...newField.mapping,
        column: columnName
      }
    });
  };

  return (
    <div className="data-dictionary">
      <div className="header">
        <h2><BookOpen size={28} /> Data Dictionary / Metadata</h2>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          <Plus size={18} /> Thêm Đối tượng
        </button>
      </div>

      <div className="entity-list">
        {entities.length === 0 ? (
          <div className="empty-state">
            <BookOpen size={64} />
            <h3>Chưa có đối tượng nào</h3>
            <p>Định nghĩa các đối tượng nghiệp vụ và metadata của bạn</p>
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              <Plus size={18} /> Thêm đối tượng đầu tiên
            </button>
          </div>
        ) : (
          <div className="cards-grid">
            {entities.map(entity => {
              const directMappings = entity.fields?.filter(f => f.mapping.type === 'direct').length || 0;
              const formulaMappings = entity.fields?.filter(f => f.mapping.type === 'formula').length || 0;
              const formattedDate = entity.updatedAt 
                ? new Date(entity.updatedAt).toLocaleDateString('vi-VN', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })
                : 'Chưa cập nhật';

              return (
                <div key={entity.id} className="card card-entity">
                  <div className="card-entity-header">
                    <div className="entity-icon">
                      <BookOpen size={24} />
                    </div>
                    <div className="entity-info">
                      <h3>{entity.name}</h3>
                      <div className="entity-meta">
                        <span className="badge badge-primary">{entity.code}</span>
                        <span className="meta-divider">•</span>
                        <span className="meta-text">{formattedDate}</span>
                      </div>
                    </div>
                    <div className="card-actions">
                      <button 
                        className="btn-icon" 
                        onClick={() => handleOpenModal(entity)}
                        title="Sửa"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        className="btn-icon btn-danger" 
                        onClick={() => handleDelete(entity.id)}
                        title="Xóa"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {entity.description && (
                    <div className="entity-description">
                      {entity.description}
                    </div>
                  )}

                  <div className="entity-stats">
                    <div className="stat-item">
                      <div className="stat-icon">
                        <Columns size={20} />
                      </div>
                      <div className="stat-content">
                        <div className="stat-value">{entity.fields?.length || 0}</div>
                        <div className="stat-label">Trường</div>
                      </div>
                    </div>
                    <div className="stat-divider"></div>
                    <div className="stat-item">
                      <div className="stat-icon">
                        <ArrowRight size={20} />
                      </div>
                      <div className="stat-content">
                        <div className="stat-value">{directMappings}</div>
                        <div className="stat-label">Direct</div>
                      </div>
                    </div>
                    <div className="stat-divider"></div>
                    <div className="stat-item">
                      <div className="stat-icon">
                        <Database size={20} />
                      </div>
                      <div className="stat-content">
                        <div className="stat-value">{formulaMappings}</div>
                        <div className="stat-label">Formula</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <BookOpen size={24} />
                {editingEntity ? 'Sửa Đối tượng' : 'Thêm Đối tượng'}
              </h3>
              <button className="btn-close" onClick={handleCloseModal}>×</button>
            </div>

            <div className="modal-body">
              {/* Entity Info Section */}
              <div style={{marginBottom: '2rem'}}>
                <h4 style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: '1rem'}}>
                  <BookOpen size={18} style={{color: 'var(--ey-yellow)'}} />
                  Thông tin cơ bản
                </h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Tên đối tượng <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="VD: Khách hàng, Hợp đồng, Giao dịch"
                    />
                  </div>

                  <div className="form-group">
                    <label>Mã đối tượng <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="VD: CUSTOMER, CONTRACT, TRANSACTION"
                      style={{textTransform: 'uppercase'}}
                    />
                    <small>Mã này sẽ được dùng làm tên sheet trong file Excel</small>
                  </div>
                </div>

                <div className="form-group">
                  <label>Mô tả</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Mô tả ngắn gọn về đối tượng này"
                    rows={3}
                  />
                </div>
              </div>

              {/* Split Layout: Fields List + Detail */}
              {formData.name && formData.code && (
                <div className="validation-split-layout">
                  {/* Left Sidebar: Fields List */}
                  <div className="fields-sidebar">
                    <h4 style={{fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                      <Columns size={16} style={{display: 'inline', marginRight: '0.25rem'}} />
                      Các trường ({formData.fields.length})
                    </h4>

                    <div className="field-selection-list">
                      {formData.fields.map((field, idx) => (
                        <div
                          key={idx}
                          className={`field-selection-card ${selectedFieldIndex === idx ? 'selected' : ''}`}
                          onClick={() => setSelectedFieldIndex(idx)}
                        >
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: 'var(--radius)',
                              background: selectedFieldIndex === idx ? 'var(--ey-gradient)' : 'var(--gray-100)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              transition: 'all 0.2s ease'
                            }}>
                              <Columns size={16} style={{color: selectedFieldIndex === idx ? 'var(--ey-black)' : 'var(--text-light)'}} />
                            </div>
                            <div style={{flex: 1, minWidth: 0}}>
                              <div style={{fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                {field.name}
                              </div>
                              <div style={{fontSize: '0.75rem', color: 'var(--text-secondary)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                {field.code}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Panel: Field Detail/Add */}
                  <div className="rules-panel">
                    {selectedFieldIndex === null ? (
                      // Add/Edit Field Form
                      <div>
                        <h4 style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem'}}>
                          {editingFieldIndex !== null ? (
                            <><Edit2 size={20} style={{color: 'var(--ey-yellow)'}} /> Sửa trường</>
                          ) : (
                            <><Plus size={20} style={{color: 'var(--ey-yellow)'}} /> Thêm trường mới</>
                          )}
                        </h4>

                        <div className="form-row">
                          <div className="form-group">
                            <label>Tên trường <span className="required">*</span></label>
                            <input
                              type="text"
                              value={newField.name}
                              onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                              placeholder="VD: Họ và tên"
                            />
                          </div>

                          <div className="form-group">
                            <label>Mã trường <span className="required">*</span></label>
                            <input
                              type="text"
                              value={newField.code}
                              onChange={(e) => setNewField({ ...newField, code: e.target.value })}
                              placeholder="VD: full_name"
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label>Kiểu dữ liệu</label>
                            <select
                              value={newField.dataType}
                              onChange={(e) => setNewField({ ...newField, dataType: e.target.value })}
                            >
                              <option value="string">String</option>
                              <option value="number">Number</option>
                              <option value="date">Date</option>
                              <option value="boolean">Boolean</option>
                            </select>
                          </div>

                          <div className="form-group">
                            <label>Format</label>
                            <input
                              type="text"
                              value={newField.format}
                              onChange={(e) => setNewField({ ...newField, format: e.target.value })}
                              placeholder="VD: text, email, phone, yyyy-mm-dd"
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Loại mapping</label>
                          <select
                            value={newField.mapping.type}
                            onChange={(e) => {
                              setNewField({
                                ...newField,
                                mapping: { 
                                  ...newField.mapping, 
                                  type: e.target.value,
                                  source: '',
                                  formula: ''
                                }
                              });
                              setSelectedSource('');
                              setSelectedTable('');
                            }}
                          >
                            <option value="direct">Direct - Trực tiếp từ nguồn</option>
                            <option value="formula">Formula - Công thức tính toán</option>
                          </select>
                        </div>

                        {newField.mapping.type === 'direct' && (
                          <div className="form-group">
                            <label>Chọn nguồn dữ liệu <span className="required">*</span></label>
                            <div className="smart-cascade-selector">
                              <div className={`cascade-step ${selectedSource ? 'selected' : ''}`}>
                                <div className="cascade-step-label">
                                  <Database size={16} />
                                  <span>Nguồn</span>
                                </div>
                                <select
                                  value={selectedSource}
                                  onChange={(e) => setSelectedSource(e.target.value)}
                                  className="cascade-select"
                                >
                                  <option value="">-- Chọn nguồn --</option>
                                  {dataSources.map(ds => (
                                    <option key={ds.id} value={ds.id}>
                                      {ds.name} ({ds.type})
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="cascade-arrow-smart">
                                <ArrowRight size={20} />
                              </div>

                              <div className={`cascade-step ${selectedTable ? 'selected' : ''} ${!selectedSource ? 'disabled' : ''}`}>
                                <div className="cascade-step-label">
                                  <Table size={16} />
                                  <span>Bảng</span>
                                </div>
                                <select
                                  value={selectedTable}
                                  onChange={(e) => setSelectedTable(e.target.value)}
                                  disabled={!selectedSource}
                                  className="cascade-select"
                                >
                                  <option value="">-- Chọn bảng --</option>
                                  {availableTables.map(table => (
                                    <option key={table.name} value={table.name}>
                                      {table.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="cascade-arrow-smart">
                                <ArrowRight size={20} />
                              </div>

                              <div className={`cascade-step ${newField.mapping.column ? 'selected' : ''} ${!selectedTable ? 'disabled' : ''}`}>
                                <div className="cascade-step-label">
                                  <Columns size={16} />
                                  <span>Trường</span>
                                </div>
                                <select
                                  value={newField.mapping.column || ''}
                                  onChange={(e) => handleColumnSelect(e.target.value)}
                                  disabled={!selectedTable}
                                  className="cascade-select"
                                >
                                  <option value="">-- Chọn trường --</option>
                                  {availableColumns.map(col => (
                                    <option key={col.name} value={col.name}>
                                      {col.name} ({col.type})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <small>Chọn theo thứ tự: Nguồn dữ liệu → Bảng → Trường</small>
                          </div>
                        )}

                        {newField.mapping.type === 'formula' && (
                          <div className="form-group">
                            <label>Công thức <span className="required">*</span></label>
                            <textarea
                              value={newField.mapping.formula || ''}
                              onChange={(e) => setNewField({
                                ...newField,
                                mapping: { ...newField.mapping, formula: e.target.value }
                              })}
                              placeholder="VD: credit_limit - used_amount"
                              rows={3}
                            />
                            <small>Sử dụng các trường khác để tính toán (VD: field1 + field2)</small>
                          </div>
                        )}

                        <div style={{display: 'flex', gap: '0.75rem', marginTop: '1.5rem'}}>
                          <button
                            className="btn btn-primary"
                            onClick={handleAddOrUpdateField}
                            disabled={
                              !newField.name || 
                              !newField.code ||
                              (newField.mapping.type === 'direct' && (!selectedSource || !selectedTable || !newField.mapping.column)) ||
                              (newField.mapping.type === 'formula' && !newField.mapping.formula)
                            }
                          >
                            {editingFieldIndex !== null ? (
                              <><Edit2 size={16} /> Cập nhật</>
                            ) : (
                              <><Plus size={16} /> Thêm trường</>
                            )}
                          </button>
                          {editingFieldIndex !== null && (
                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                resetFieldForm();
                                setSelectedFieldIndex(null);
                              }}
                            >
                              Hủy
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      // Field Detail View
                      <div>
                        {formData.fields[selectedFieldIndex] && (
                          <>
                            {/* Field Info Header */}
                            <div style={{
                              padding: '1rem',
                              background: 'linear-gradient(135deg, rgba(255,230,0,0.1) 0%, rgba(255,230,0,0.05) 100%)',
                              borderRadius: 'var(--radius-lg)',
                              border: '1px solid rgba(255,230,0,0.3)',
                              marginBottom: '1.5rem'
                            }}>
                              <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                                <div style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: 'var(--radius)',
                                  background: 'var(--ey-gradient)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <Columns size={20} style={{color: 'var(--ey-black)'}} />
                                </div>
                                <div style={{flex: 1}}>
                                  <div style={{fontWeight: 700, fontSize: '1.125rem', marginBottom: '0.25rem'}}>
                                    {formData.fields[selectedFieldIndex].name}
                                  </div>
                                  <div style={{fontSize: '0.875rem', color: 'var(--text-secondary)', fontFamily: 'monospace'}}>
                                    {formData.fields[selectedFieldIndex].code}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Field Details */}
                            <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem'}}>
                              <div>
                                <label style={{fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block'}}>
                                  Kiểu dữ liệu
                                </label>
                                <div>
                                  <span className="badge badge-info" style={{marginRight: '0.5rem'}}>
                                    {formData.fields[selectedFieldIndex].dataType}
                                  </span>
                                  <span style={{fontSize: '0.875rem', color: 'var(--text-light)'}}>
                                    {formData.fields[selectedFieldIndex].format}
                                  </span>
                                </div>
                              </div>

                              <div>
                                <label style={{fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem', display: 'block'}}>
                                  Mapping
                                </label>
                                {formData.fields[selectedFieldIndex].mapping.type === 'direct' ? (
                                  <div>
                                    <span className="badge badge-success" style={{marginBottom: '0.5rem'}}>Direct</span>
                                    <div style={{
                                      padding: '0.75rem',
                                      background: 'var(--gray-50)',
                                      borderRadius: 'var(--radius)',
                                      fontSize: '0.875rem',
                                      fontFamily: 'monospace',
                                      color: 'var(--text)',
                                      wordBreak: 'break-all'
                                    }}>
                                      {formData.fields[selectedFieldIndex].mapping.source}
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <span className="badge badge-warning" style={{marginBottom: '0.5rem'}}>Formula</span>
                                    <div style={{
                                      padding: '0.75rem',
                                      background: 'var(--gray-50)',
                                      borderRadius: 'var(--radius)',
                                      fontSize: '0.875rem',
                                      fontFamily: 'monospace',
                                      color: 'var(--text)',
                                      wordBreak: 'break-all'
                                    }}>
                                      {formData.fields[selectedFieldIndex].mapping.formula}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Actions */}
                            <div style={{display: 'flex', gap: '0.75rem', marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-light)'}}>
                              <button
                                className="btn btn-primary"
                                onClick={() => handleEditField(selectedFieldIndex)}
                              >
                                <Edit2 size={16} /> Sửa trường
                              </button>
                              <button
                                className="btn btn-danger"
                                onClick={() => {
                                  if (window.confirm(`Xóa trường "${formData.fields[selectedFieldIndex].name}"?`)) {
                                    handleRemoveField(selectedFieldIndex);
                                    setSelectedFieldIndex(null);
                                  }
                                }}
                              >
                                <Trash2 size={16} /> Xóa
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                Hủy
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={!formData.name || !formData.code}
              >
                {editingEntity ? 'Cập nhật' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataDictionary;
